import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { actions } from "../../../app/categoriesSlice.js";
import CoreFilter from "../../../app/core/components/CoreFilter/CoreFilter.jsx";
import AutocompleteFilter from "../../../app/core/components/CoreFilter/components/AutocompleteFilter/AutocompleteFilter.jsx";
import CheckboxFilter from "../../../app/core/components/CoreFilter/components/CheckboxFilter/CheckboxFilter.jsx";
import CoreSpinner from "../../../app/core/components/CoreSpinner/CoreSpinner.jsx";
import { actions as actionsTypologies } from "../../../app/enumsSlice.js";
import { TABLE_ROWS_NUMBER } from "../../../utils/constants/tables.js";
import {
  useGetAdminCategoriesQuery,
  useGetAdminTypologiesQuery,
} from "../../api/admin/categoriesApi.js";

import { Tab, Tabs } from "@mui/material";
import { t } from "i18next";
import CoreSearchBar from "../../../app/core/components/CoreSearch/CoreSearch.jsx";
import useSyncFilterWithGlobalFilters from "../../../utils/hookCustom.js";
import { useGetCommercialistaQuery } from "../../api/admin/gestisciCommercialistiApi.js";
import {
  useDeleteAllDocumentsMutation,
  useGetDocumentsDataQuery,
  useReanalyzeAllDocumentMutation,
  useRejectAllDocumentMutation,
  useValidateAllDocumentMutation,
} from "../../api/admin/gestisciDocumentiApi.js";
import { useGetStudiosQuery } from "../../api/admin/gestisciStudioApi.js";
import { useGetUsersQuery } from "../../api/admin/gestisciUtentiApi.js";
import { selectAllFilters } from "../../../app/globalFiltersSlice.js";
import {
  removeGlobalFilter,
  setMultipleGlobalFilters,
} from "../../../app/globalFiltersSlice.js";
import { ROLE } from "../../../utils/constants/profiles.js";
import GestioneDocumentiButtonCockpit from "./GestioneDocumentiTable/ControlCockpit/GestioneDocumentiButtonCockpit.jsx";
import GestioneDocumentiStats from "./GestioneDocumentiTable/ControlCockpit/GestioneDocumentiStats.jsx";
import GestioneDocumentiTable from "./GestioneDocumentiTable/Table/GestioneDocumentiTable.jsx";

const GestioneDocumentiPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = useSelector((state) => state.app.role);
  const appConfig = useSelector((state) => state.app.appConfig);
  const globalFilters = useSelector(selectAllFilters);
  const selectedUser = location.state ? location?.state : "";
  const [isPolling, setIsPolling] = useState(false);
  const [pollingCounter, setPollingCounter] = useState(0);
  const [debouncedInputStudioValue, setDebouncedInputStudioValue] =
    useState("");
  const [debouncedInputOperatorValue, setDebouncedInputOperatorValue] =
    useState("");
  const [debouncedInputUserValue, setDebouncedInputUserValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const folderEnabled = appConfig?.modules?.documents?.folders;

  const [page, setPage] = useState({
    currentPage: 1,
    startRow: 0,
    endRow: TABLE_ROWS_NUMBER,
  });
  const [filter, setFilter] = useState({
    mainCategoryId: "",
    subCategoryId: "",
    studioId: "",
    adminId: "",
    status: "",
    userId: !selectedUser ? "" : selectedUser.id,
    practiceId: "",
    dateFrom: "",
    dateTo: "",
    read: {},
    referenceYear: "",
  });
  const [additionalFilters, setAdditionalFilters] = useState({
    name: { filterType: "string", type: "contains", filter: "" },
  });
  const [orderDirection, setOrderDirection] = useState("desc");
  const [valueToOrderBy, setValueToOrderBy] = useState("createdAt");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 750);
    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Data from API

  const { data, isLoading, isFetching, refetch } = useGetDocumentsDataQuery(
    {
      page,
      filter,
      orderDirection,
      valueToOrderBy,
      additionalFilters,
      query: debouncedSearchTerm,
    },
    { refetchOnMountOrArgChange: true }
  );

  const { data: usersData } = useGetUsersQuery(
    {
      page: page,
      value: debouncedInputUserValue,
    },
    { refetchOnMountOrArgChange: true }
  );

  const { data: adminCategories } = useGetAdminCategoriesQuery();

  const { data: adminStudio } = useGetStudiosQuery({
    ...page,
    value: debouncedInputStudioValue,
  });

  const { data: operatorsData } = useGetCommercialistaQuery({
    page: page,
    value: debouncedInputOperatorValue,
  });

  const { data: adminTypologies } = useGetAdminTypologiesQuery();

  const [deleteAllDocuments, { isLoading: isDeleteLoading }] =
    useDeleteAllDocumentsMutation();
  const [validateAllDocument, { isLoading: isValidateAllDocumentLoading }] =
    useValidateAllDocumentMutation();
  const [rejectAllDocument, { isLoading: isRejectAllDocumentLoading }] =
    useRejectAllDocumentMutation();
  const [reanalyzeAllDocument, { isLoading: isReanalyzeAllDocumentLoading }] =
    useReanalyzeAllDocumentMutation();

  useEffect(() => {
    if (adminCategories?.Data)
      dispatch(actions.setMacroCategories(adminCategories?.Data));

    if (adminTypologies?.Data)
      dispatch(actionsTypologies.setIdentTypologies(adminTypologies?.Data));
  }, [adminCategories, adminTypologies, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsPolling(true);
      if (pollingCounter < 8) {
        refetch();
        setPollingCounter((prevCounter) => prevCounter + 1);
      } else {
        setIsPolling(false);
        clearInterval(interval);
      }
    }, 3000);
    return () => {
      clearInterval(interval);
    };
  }, [refetch, pollingCounter]);

  useSyncFilterWithGlobalFilters(setFilter);

  const handleFiltersChange = (newFilters) => {
    // Trasforma i filtri nel formato desiderato
    const transformedFilters = transformFilters(newFilters);

    // Ora puoi usare l'oggetto trasformato
    setFilter(transformedFilters);
  };

  const handleGlobalFiltersApply = (filters) => {
    // Separa i filtri con valori validi da quelli undefined/vuoti
    const validFilters = [];
    const filtersToRemove = [];

    filters.forEach((filter) => {
      if (filter.value !== undefined && filter.value !== "") {
        // Filtro con valore valido - mantienilo
        validFilters.push(filter);
      } else {
        // Filtro undefined o vuoto - rimuovilo
        filtersToRemove.push(filter.key);
      }
    });

    // Rimuovi i filtri undefined/vuoti dallo state
    filtersToRemove.forEach((filterKey) => {
      dispatch(removeGlobalFilter(filterKey));
    });

    // Imposta solo i filtri con valori validi
    if (validFilters.length > 0) {
      dispatch(setMultipleGlobalFilters(validFilters));
    }
  };

  const transformFilters = (filtersArray) => {
    const filterObject = {
      mainCategoryId: "",
      subCategoryId: "",
      studioId: "",
      adminId: "",
      userId: "",
      referenceYear: "",
      practiceId: "",
      status: "",
      dateFrom: "",
      dateTo: "",
      read: {},
    };

    filtersArray.forEach((filter) => {
      if (!filter?.key) return;

      if (filter.key === "dateFrom") {
        if (filter.value) {
          const year = filter.value.toString();
          if (/^\d{4}$/.test(year)) {
            filterObject.dateFrom = `${year}-01-01T00:00:00.000Z`;
            filterObject.dateTo = `${year}-12-31T23:59:59.999Z`;
          } else {
            filterObject.dateFrom = filter.value;
            filterObject.dateTo = filter.value;
          }
        } else {
          filterObject.dateFrom = "";
          filterObject.dateTo = "";
        }
      } else if (filter.key === "read") {
        const readFilter = { filterType: "bool", type: "equals", filter: true };
        filterObject.read = readFilter;
        setAdditionalFilters((prev) => ({
          ...prev,
          read: readFilter,
        }));
      } else if (
        Object.prototype.hasOwnProperty.call(filterObject, filter.key)
      ) {
        // Se il valore non è definito, mantieni stringa vuota
        filterObject[filter.key] = filter.value ?? "";
      }
    });

    return filterObject;
  };

  const generateAnniDiRiferimento = (
    startYear,
    endYear,
    insertGenericLabel
  ) => {
    const objectsArray = [];
    for (let year = startYear; year <= endYear; year++) {
      objectsArray.push({
        label:
          new Date().getFullYear() === year
            ? `Anno Corrente (spese fiscali ${year - 1})`
            : `${year} (spese fiscali ${year - 1})`,
        value: year,
      });
    }
    insertGenericLabel &&
      objectsArray.unshift({ label: "GENERICO", value: "GENERICO" });
    return objectsArray;
  };

  const anniDiRiferimento = generateAnniDiRiferimento(
    new Date().getFullYear() - 4,
    new Date().getFullYear() + 6
  );

  const YEARS_FOR_SELECT = () => {
    const currentYear = new Date().getFullYear();
    const yearsAvailable = [];
    for (let adder = -5; adder < 3; adder++) {
      yearsAvailable.push({
        value: currentYear + adder,
        label: (currentYear + adder).toString(),
      });
    }
    return yearsAvailable;
  };

  const yearsForSelect = YEARS_FOR_SELECT();

  const statusOptions = [
    { label: t("ToValidate"), value: 3 },
    { label: t("Validateds"), value: 5 },
    { label: t("Discardeds"), value: 6 },
  ];

  if (isLoading) {
    return <CoreSpinner />;
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center">
        <h2 style={{ width: "200px" }}>{t("Archive")}</h2>
        {folderEnabled && (
          <Tabs value={0}>
            <Tab className="text-mont text-capitalize fs-6" label={"Tabella"} />
            <Tab
              className="text-mont text-capitalize fs-6"
              label={"Cartelle"}
              onClick={() => navigate("folder")}
            />
          </Tabs>
        )}
      </div>

      <div>
        <div className="flex flex-row -mt-16 justify-end space-x-2">
          <CoreFilter
            onFiltersChange={handleFiltersChange}
            onGlobalFiltersApply={handleGlobalFiltersApply}
            initialFilters={globalFilters}
          >
            {ROLE.ADMIN === role && (
              <AutocompleteFilter
                label="Cerca Studio"
                placeholder="Digita nome studio..."
                options={adminStudio?.Data?.items}
                filterKey="studioId"
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name}
                allowGlobal={true}
              />
            )}
            {role !== ROLE.OPERATOR && (
              <AutocompleteFilter
                label="Cerca Operatore"
                placeholder="Digita nome operatore..."
                options={operatorsData?.Data?.items}
                filterKey="adminId"
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => `${option.name} ${option.surname}`}
                allowGlobal={true}
              />
            )}

            <AutocompleteFilter
              label="Cerca Cliente"
              placeholder="Digita nome cliente..."
              options={usersData?.Data?.items}
              filterKey="userId"
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => `${option.name} ${option.surname}`}
              allowGlobal={true}
            />

            <AutocompleteFilter
              label="Anno Documento"
              placeholder="Seleziona anno..."
              options={yearsForSelect}
              filterKey="dateFrom"
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
            />
            <AutocompleteFilter
              label={t("ReferenceYear")}
              placeholder="Seleziona anno..."
              options={anniDiRiferimento}
              filterKey="referenceYear"
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
            />
            <AutocompleteFilter
              label={t("DocStatus")}
              placeholder="Seleziona stato..."
              options={statusOptions}
              filterKey="status"
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
            />
            <CheckboxFilter
              filterKey="read"
              label={t("FilterImportantDoc")}
            ></CheckboxFilter>
          </CoreFilter>
        </div>

        <div className="w-full flex items-center justify-between mt-5">
          <div style={{ flex: 3 }}>
            <CoreSearchBar
              isLoading={!isPolling && (isLoading || isFetching)}
              placeholder={t("SearchDocExt")}
              searchTerm={searchTerm}
              handleChange={setSearchTerm}
            />
          </div>
          <div style={{ flex: 5, marginRight: 6 }}>
            <GestioneDocumentiButtonCockpit
              data={data?.Data?.items}
              refetch={refetch}
              isDeleteLoading={isDeleteLoading}
              selectedUser={selectedUser}
              deleteDocuments={deleteAllDocuments}
              validateAllDocument={validateAllDocument}
              rejectAllDocument={rejectAllDocument}
              reanalyzeAllDocument={reanalyzeAllDocument}
            />
          </div>

          <GestioneDocumentiStats
            data={data?.Data}
            isPolling={isPolling}
            pollingCounter={pollingCounter}
          />
        </div>

        <GestioneDocumentiTable
          page={page}
          setPage={setPage}
          data={data?.Data}
          valueToOrderBy={valueToOrderBy}
          setValueToOrderBy={setValueToOrderBy}
          orderDirection={orderDirection}
          setOrderDirection={setOrderDirection}
          refetch={refetch}
          isLoading={
            isLoading ||
            isDeleteLoading ||
            isRejectAllDocumentLoading ||
            isValidateAllDocumentLoading ||
            isReanalyzeAllDocumentLoading
          }
          totalPages={Math.ceil(data?.Data?.count / TABLE_ROWS_NUMBER) || 1}
          isFetching={isFetching}
          isPolling={isPolling}
        />
      </div>
    </div>
  );
};

export default GestioneDocumentiPage;
